import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Bienvenue, memoireBienvenue } from '../accueil/Bienvenue';
import { CarrouselDossiers } from '../accueil/CarrouselDossiers';
import type { DossierEnCarte } from '../accueil/CarteDossier';
import { maintenant } from '../horloge';
import { Bouton, Carte, Galets } from '../ui';
import { useSession } from '../auth/session';


/**
 * Accueil — la liste des dossiers.
 *
 * Le ton compte jusque dans les cas d'erreur : annoncer « vos dossiers n'ont pas pu être
 * chargés » à quelqu'un qui n'est simplement pas connecté décrit un échec là où il n'y a
 * qu'une étape à franchir. On propose donc l'action utile plutôt que le constat négatif.
 */
export function Accueil() {
  const { client, profil } = useSession();
  // `null` = pas de présentation ouverte. On distingue la première venue d'une réouverture
  // demandée : la formulation du bouton de sortie n'est pas la même.
  const [presentation, setPresentation] = useState<'auto' | 'demandee' | null>(null);
  // L'ouverture automatique n'a lieu qu'une fois par visite, quoi qu'il arrive ensuite.
  // Sans ce verrou, un navigateur qui refuse le stockage rouvrirait la présentation au
  // rendu suivant sa fermeture — indéfiniment.
  const proposee = useRef(false);

  const dossiers = useQuery({
    queryKey: ['case-files'],
    queryFn: () => client.request<DossierEnCarte[]>('/case-files'),
    // Sans session, rien à demander : la requête n'a pas lieu d'être tentée.
    enabled: Boolean(profil),
  });

  const vide = dossiers.data?.length === 0;

  // Ouverture automatique à la première venue seulement. Le rendu qui suit la réponse de
  // l'API est le bon moment : plus tôt, on ouvrirait une présentation à quelqu'un qui a
  // déjà des dossiers dès que le réseau est lent.
  if (vide && !proposee.current && !memoireBienvenue.dejaVue()) {
    proposee.current = true;
    setPresentation('auto');
  }

  // Sans profil, cet écran n'a rien à montrer : la garde de route emmène à la connexion,
  // et la déconnexion y navigue elle-même. Un encart « connectez-vous » proposé ici
  // apparaissait le temps d'un rendu au milieu d'une déconnexion — on croyait avoir échoué
  // à se déconnecter, alors qu'on partait.
  if (!profil) return null;

  const instant = maintenant();
  const jour = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(instant);

  return (
    <>
      {/* Le salut passe en surtitre : « Bonjour Jacques » n'est pas ce qu'on vient
          chercher. La date, elle, cadre tout ce qui suit — la garde du jour s'y rapporte. */}
      <p className="accueil-salut">Bonjour {profil.displayName}</p>
      <h1 className="accueil-jour">{jour}</h1>

      {dossiers.isPending ? <p className="discret">Chargement de vos dossiers…</p> : null}

      {dossiers.isError ? (
        <Carte titre="Vos dossiers arrivent dans un instant">
          <p style={{ marginTop: 0 }}>
            La connexion au serveur n'a pas abouti cette fois-ci. Vos données sont
            intactes ; il suffit de réessayer.
          </p>
          <Bouton onClick={() => void dossiers.refetch()}>Réessayer</Bouton>
        </Carte>
      ) : null}

      {/* Pas de carte ici. Une carte sépare un objet de ses voisins ; à la première venue
          il n'y a pas de voisin — cette invitation est tout l'écran. Le liseré n'encadrait
          donc rien, il redessinait la page à l'intérieur de la page. Voir
          `.accueil-premier`. */}
      {vide ? (
        <section className="accueil-premier">
          {/* L'illustration précède le titre, et c'est le seul écran où elle a sa place :
              celui d'un parent qui vient d'arriver et n'a encore rien. Partout ailleurs
              l'accueil sert à entrer, pas à contempler. */}
          <Galets />
          <h2>Votre premier dossier</h2>
          <p>
            Un dossier réunit vos enfants, le calendrier de garde et les échanges avec
            l'autre parent.
          </p>
          <Link to="/dossiers/creer" className="bouton">
            Créer un dossier
          </Link>
          {/* Revoir la présentation n'est pas une seconde action : c'est un retour en
              arrière pour qui n'a pas suivi. En bouton bordé, elle pesait autant que la
              seule chose à faire ici. */}
          <Bouton variante="lien" onClick={() => setPresentation('demandee')}>
            Revoir la présentation
          </Bouton>
        </section>
      ) : null}

      {presentation ? (
        <Bienvenue
          ouverteParDefaut={presentation === 'auto'}
          onFermer={() => {
            memoireBienvenue.retenir();
            setPresentation(null);
          }}
        />
      ) : null}

      {/* Le long paragraphe du mode parent seul a quitté l'accueil : il vit désormais
          dans le dossier, sur « Aujourd'hui », où il est utile. Ici, une puce suffit —
          l'accueil sert à entrer, pas à expliquer. */}
      <CarrouselDossiers dossiers={dossiers.data ?? []} maintenant={instant} />

      {/* « Nouveau dossier » a quitté cet écran pour le menu (`MenuCompte.tsx`).
          On ouvre un dossier une ou deux fois dans sa vie : le bouton occupait en
          permanence l'écran le plus consulté du produit, au même rang que les gestes du
          jour. Son avertissement — un dossier vaut pour **un** autre parent, un frère ou
          une sœur s'ajoute dans le dossier, à « Enfants » — l'a suivi jusqu'à l'écran de
          création, où la méprise se commet et où elle est encore rattrapable
          (`creerDossier.etape1.aideFratrie`). Le laisser ici aurait été deux endroits
          pour une même mise en garde, dont un que personne ne lit.

          Reste la sortie vers la présentation : elle ne mène nulle part et ne se range
          donc pas dans un menu d'écrans. */}
      {(dossiers.data ?? []).length > 0 ? (
        <div className="accueil-actions">
          <Bouton variante="lien" onClick={() => setPresentation('demandee')}>
            Revoir la présentation
          </Bouton>
        </div>
      ) : null}
    </>
  );
}
